import { useCallback, useEffect, useRef, useState } from "react";
import {
  disconnectBusinessPerformerCalendar,
  disconnectBusinessPerformerDrive,
  disconnectBusinessPerformerGmail,
  disconnectSlack,
  getCalendarStatus,
  getGmailStatus,
  getPerformerRunResult,
  getSlackConnectionStatus,
  isGmailReadyForPerformer,
  startPerformerRun,
} from "../businessModeService";
import { maybeRedirectOnSessionAuthError } from "../../core/services/sessionExpiry";
import config from "../../core/config";
import { useAuth } from "../../core/context/AuthContext";
import {
  readConnectorSnapshot,
  writeConnectorSnapshot,
} from "../../core/utils/connectorSnapshotCache";
import {
  PERFORMER_CALENDAR_OAUTH_CONNECTED_EVENT,
  PERFORMER_DRIVE_OAUTH_CONNECTED_EVENT,
  PERFORMER_GMAIL_OAUTH_CONNECTED_EVENT,
  startPerformerCalendarOAuthFlow,
  startPerformerDriveOAuthFlow,
  startPerformerGmailOAuthFlow,
  startPerformerSlackOAuthFlow,
} from "./performerGmailOAuth";
import {
  buildPerformerActionInput,
  resolvePerformerItemIds,
} from "./shared/performerActionInput";
import { clearGateChoice } from "./shared/connectorRunGate";
import { performerRunErrorMessage } from "./shared/UsageLimitBanner";

export const POLL_INTERVAL_MS = 3000;

export const STEP_IDLE = "idle";
export const STEP_CHECKING_GMAIL = "checking_gmail";
export const STEP_NEED_GMAIL = "need_gmail";
export const STEP_DRIVE_INPUT = "drive_input";
export const STEP_RUNNING = "running";
export const STEP_DONE = "done";
export const STEP_ERROR = "error";

/**
 * True when the UI already has a performer result for this item.
 *
 * @example
 * itemHasStoredPerformerResult(item, { "wf::k::f": { draft: "..." } }, "");
 * // true
 */
export const itemHasStoredPerformerResult = (item, resultsMap, contextWorkflowId) => {
  if (!resultsMap || typeof resultsMap !== "object") return false;
  const { stableId: cid } = resolvePerformerItemIds(item, contextWorkflowId);
  const actionText = String(item?.action_item || "").trim();
  const legacyKeyFile = `${String(item?.key || "")}::${String(item?.file_id || "")}`;
  const has = (key) => {
    const v = resultsMap[key];
    return v != null && typeof v === "object";
  };
  if (cid && has(cid)) return true;
  if (actionText && has(actionText)) return true;
  if (legacyKeyFile !== "::" && has(legacyKeyFile)) return true;
  return false;
};

/**
 * Copy of `aiResultsByItem` with lookup keys removed for bulk-run rows.
 *
 * @example
 * stripPerformerInputsFromResultsMap({ "w::k::f": {} }, [{ client_item_id: "w::k::f" }]);
 * // {}
 */
export const stripPerformerInputsFromResultsMap = (prev, actionItemInputs) => {
  if (!prev || typeof prev !== "object") return {};
  if (!Array.isArray(actionItemInputs) || actionItemInputs.length === 0) return { ...prev };
  const next = { ...prev };
  actionItemInputs.forEach((row) => {
    const cid = String(row?.client_item_id || "").trim();
    const text = String(row?.item || "").trim();
    const legacy = `${String(row?.key || "")}::${String(row?.file_id || "")}`;
    if (cid) delete next[cid];
    if (text) delete next[text];
    if (legacy !== "::") delete next[legacy];
  });
  return next;
};

const parseDriveUrlsFromText = (driveInput) =>
  String(driveInput || "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("http"));

/**
 * Gmail check, Drive context, and bulk performer run state for the AI drafts panel.
 *
 * @example
 * const setup = usePerformerSetup({
 *   pendingItems,
 *   aiResultsByItem,
 *   onResultsReady,
 *   onBulkRunStart,
 *   onBulkRunFinished,
 *   onGmailAuthUrl,
 *   autoCheckGmail: true,
 * });
 * // setup.gmailReady, setup.handleRunPerformer('all'), setup.driveInput, ...
 */
export const usePerformerSetup = ({
  pendingItems,
  onResultsReady,
  onProgressReady,
  onBulkRunStart,
  onBulkRunFinished,
  onGmailAuthUrl,
  workflowId: contextWorkflowId = "",
  aiResultsByItem = {},
  autoCheckGmail = false,
  enabled = true,
}) => {
  // Last-known connection status for this user: General settings paints it immediately and
  // the live check below corrects it quietly (same stale-while-revalidate as the connector list).
  const authUser = useAuth()?.user;
  const userKey = String(authUser?.id || authUser?.email || "").trim();
  const [cachedStatus] = useState(() => readConnectorSnapshot(userKey)?.performerStatus || null);
  const [step, setStep] = useState(() =>
    cachedStatus ? (cachedStatus.gmailReady === true ? STEP_DRIVE_INPUT : STEP_NEED_GMAIL) : STEP_IDLE
  );
  const [statusLive, setStatusLive] = useState(false);
  const [error, setError] = useState("");
  const [driveInput, setDriveInput] = useState("");
  const [contextFiles, setContextFiles] = useState([]);
  const [driveStepNotice, setDriveStepNotice] = useState("");
  const [runTargetCount, setRunTargetCount] = useState(null);
  const [runLaunchMode, setRunLaunchMode] = useState(null);
  const [gmailAuthUrl, setGmailAuthUrl] = useState("");
  const [driveAuthUrl, setDriveAuthUrl] = useState("");
  const [gmailReady, setGmailReady] = useState(cachedStatus?.gmailReady === true);
  const [driveReady, setDriveReady] = useState(cachedStatus?.driveReady === true);
  const [calendarReady, setCalendarReady] = useState(cachedStatus?.calendarReady === true);
  const [calendarConnectLoading, setCalendarConnectLoading] = useState(false);
  const [gmailConnectLoading, setGmailConnectLoading] = useState(false);
  const [slackReady, setSlackReady] = useState(cachedStatus?.slackReady === true);
  const [slackHasSendScope, setSlackHasSendScope] = useState(cachedStatus?.slackHasSendScope === true);
  const [slackConnectLoading, setSlackConnectLoading] = useState(false);
  const pollRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const gmailPollRef = useRef(null);
  const calendarPollRef = useRef(null);
  const slackPollRef = useRef(null);
  const mountedRef = useRef(true);
  const hasCachedStatusRef = useRef(Boolean(cachedStatus));

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
      if (gmailPollRef.current) {
        if (typeof gmailPollRef.current === "function") gmailPollRef.current();
        else clearInterval(gmailPollRef.current);
      }
      if (calendarPollRef.current) {
        if (typeof calendarPollRef.current === "function") calendarPollRef.current();
        else clearInterval(calendarPollRef.current);
      }
      if (slackPollRef.current) {
        if (typeof slackPollRef.current === "function") slackPollRef.current();
        else clearInterval(slackPollRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!statusLive) return;
    writeConnectorSnapshot(userKey, {
      performerStatus: { gmailReady, driveReady, calendarReady, slackReady, slackHasSendScope },
    });
  }, [userKey, statusLive, gmailReady, driveReady, calendarReady, slackReady, slackHasSendScope]);

  const refreshCalendarStatus = useCallback(async () => {
    try {
      const status = await getCalendarStatus();
      if (!mountedRef.current) return;
      setCalendarReady(status?.connected === true || status?.calendar_authorized === true);
    } catch (_) {
      if (!mountedRef.current) return;
      setCalendarReady(false);
    }
  }, []);

  const refreshGmailStatus = useCallback(async () => {
    if (!hasCachedStatusRef.current) setStep(STEP_CHECKING_GMAIL);
    setError("");
    try {
      const [status, authResp] = await Promise.all([
        getGmailStatus({ verify: "performer" }),
        fetch(`${config.BACKEND_URL}/externalauth/gmail/auth_url?purpose=performer`, {
          method: "GET",
          credentials: "include",
        }),
      ]);
      const authPayload = await authResp.json();
      if (!mountedRef.current) return;
      const url = authPayload.auth_url || "";
      setGmailAuthUrl(url);
      if (onGmailAuthUrl) onGmailAuthUrl(url);
      const ready = isGmailReadyForPerformer(status);
      setStatusLive(true);
      setGmailReady(ready);
      setDriveReady(status.drive_authorized === true);
      void refreshCalendarStatus();
      if (ready) {
        setStep(STEP_DRIVE_INPUT);
      } else {
        if (status.gmail_api_error) setError(status.gmail_api_error);
        setStep(STEP_NEED_GMAIL);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(String(err?.message || "Failed to check Gmail status"));
      setStep(STEP_ERROR);
      setGmailReady(false);
    }
  }, [onGmailAuthUrl, refreshCalendarStatus]);

  useEffect(() => {
    if (!autoCheckGmail) return;
    void refreshGmailStatus();
  }, [autoCheckGmail, refreshGmailStatus]);

  useEffect(() => {
    const onGmailConnected = async () => {
      if (!mountedRef.current) return;
      setGmailConnectLoading(false);
      setError("");
      try {
        const status = await getGmailStatus({ verify: "performer" });
        if (!mountedRef.current) return;
        const ready = isGmailReadyForPerformer(status);
        setGmailReady(ready);
        setDriveReady(status.drive_authorized === true);
        if (ready) {
          setStep(STEP_DRIVE_INPUT);
          return;
        }
        setError(status.gmail_api_error || "Gmail permissions are still incomplete.");
        setStep(STEP_NEED_GMAIL);
      } catch (err) {
        if (!mountedRef.current) return;
        setError(String(err?.message || "Failed to verify Gmail connection"));
        setStep(STEP_NEED_GMAIL);
        setGmailReady(false);
      }
    };
    const onDriveConnected = async () => {
      if (!mountedRef.current) return;
      try {
        const status = await getGmailStatus({ verify: "performer" });
        if (!mountedRef.current) return;
        setDriveReady(status.drive_authorized === true);
      } catch (_) {
        // ignore
      }
    };
    const onCalendarConnected = async () => {
      if (!mountedRef.current) return;
      setCalendarConnectLoading(false);
      await refreshCalendarStatus();
    };
    window.addEventListener(PERFORMER_GMAIL_OAUTH_CONNECTED_EVENT, onGmailConnected);
    window.addEventListener(PERFORMER_DRIVE_OAUTH_CONNECTED_EVENT, onDriveConnected);
    window.addEventListener(PERFORMER_CALENDAR_OAUTH_CONNECTED_EVENT, onCalendarConnected);
    return () => {
      window.removeEventListener(PERFORMER_GMAIL_OAUTH_CONNECTED_EVENT, onGmailConnected);
      window.removeEventListener(PERFORMER_DRIVE_OAUTH_CONNECTED_EVENT, onDriveConnected);
      window.removeEventListener(PERFORMER_CALENDAR_OAUTH_CONNECTED_EVENT, onCalendarConnected);
    };
  }, [refreshCalendarStatus]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollInFlightRef.current = false;
  }, []);

  const pollRunOnce = useCallback(
    async (id) => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        const data = await getPerformerRunResult(id);
        if (!mountedRef.current) return;
        if (data.result?.results?.length) {
          onResultsReady(data.result);
        }
        if (data.progress && onProgressReady) {
          onProgressReady(data.progress);
        }
        if (data.status === "done") {
          stopPolling();
          setStep(STEP_DONE);
          onBulkRunFinished?.();
        } else if (data.status === "error") {
          stopPolling();
          setRunTargetCount(null);
          setError(data.error || "Run failed");
          setStep(STEP_ERROR);
          onBulkRunFinished?.();
        }
      } catch (err) {
        if (!mountedRef.current) return;
        stopPolling();
        setRunTargetCount(null);
        const message = String(err?.message || "Polling failed");
        if (maybeRedirectOnSessionAuthError(message)) {
          onBulkRunFinished?.();
          return;
        }
        setError(message);
        setStep(STEP_ERROR);
        onBulkRunFinished?.();
      } finally {
        pollInFlightRef.current = false;
      }
    },
    [stopPolling, onResultsReady, onProgressReady, onBulkRunFinished]
  );

  const startPolling = useCallback(
    (id) => {
      stopPolling();
      void pollRunOnce(id);
      pollRef.current = setInterval(() => {
        void pollRunOnce(id);
      }, POLL_INTERVAL_MS);
    },
    [stopPolling, pollRunOnce]
  );

  const stopGmailPolling = useCallback(() => {
    if (!gmailPollRef.current) return;
    if (typeof gmailPollRef.current === "function") {
      gmailPollRef.current();
    } else {
      clearInterval(gmailPollRef.current);
    }
    gmailPollRef.current = null;
  }, []);

  useEffect(() => {
    if (enabled) return;
    stopPolling();
    stopGmailPolling();
  }, [enabled, stopPolling, stopGmailPolling]);

  const handleGmailConnect = useCallback(() => {
    if (!gmailAuthUrl || gmailConnectLoading) return;
    stopGmailPolling();
    setGmailConnectLoading(true);
    gmailPollRef.current = startPerformerGmailOAuthFlow(
      gmailAuthUrl,
      () => {
        if (mountedRef.current) {
          setGmailConnectLoading(false);
          setError("");
          setGmailReady(true);
          setStep(STEP_DRIVE_INPUT);
        }
      },
      () => {
        if (mountedRef.current) {
          setGmailConnectLoading(false);
        }
      }
    );
  }, [gmailAuthUrl, gmailConnectLoading, stopGmailPolling]);

  const handleGmailDisconnect = useCallback(async () => {
    setError("");
    try {
      await disconnectBusinessPerformerGmail();
      if (!mountedRef.current) return;
      setGmailReady(false);
      setStep(STEP_NEED_GMAIL);
      clearGateChoice("gmail");
      await refreshGmailStatus();
    } catch (err) {
      if (!mountedRef.current) return;
      setError(String(err?.message || "Failed to disconnect Gmail"));
    }
  }, [refreshGmailStatus]);

  const handleDriveConnect = useCallback(async () => {
    let authUrl = driveAuthUrl;
    if (!authUrl) {
      const authResp = await fetch(
        `${config.BACKEND_URL}/externalauth/gmail/auth_url?purpose=drive`,
        { method: "GET", credentials: "include" }
      );
      const authPayload = await authResp.json();
      authUrl = authPayload.auth_url || "";
      if (mountedRef.current) {
        setDriveAuthUrl(authUrl);
      }
    }
    if (!authUrl) return;
    startPerformerDriveOAuthFlow(authUrl, () => {
      if (mountedRef.current) {
        setDriveReady(true);
      }
    });
  }, [driveAuthUrl]);

  const handleDriveDisconnect = useCallback(async () => {
    setError("");
    try {
      await disconnectBusinessPerformerDrive();
      if (!mountedRef.current) return;
      setDriveReady(false);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(String(err?.message || "Failed to disconnect Drive"));
    }
  }, []);

  const stopCalendarPolling = useCallback(() => {
    if (!calendarPollRef.current) return;
    if (typeof calendarPollRef.current === "function") {
      calendarPollRef.current();
    } else {
      clearInterval(calendarPollRef.current);
    }
    calendarPollRef.current = null;
  }, []);

  const handleCalendarConnect = useCallback(async () => {
    if (calendarConnectLoading) return;
    stopCalendarPolling();
    setCalendarConnectLoading(true);
    try {
      const authResp = await fetch(
        `${config.BACKEND_URL}/externalauth/google_calendar/auth_url`,
        { method: "GET", credentials: "include" }
      );
      const authPayload = await authResp.json();
      const authUrl = authPayload.auth_url || "";
      if (!authUrl) {
        if (mountedRef.current) setCalendarConnectLoading(false);
        return;
      }
      calendarPollRef.current = startPerformerCalendarOAuthFlow(authUrl, () => {
        if (mountedRef.current) {
          setCalendarConnectLoading(false);
          setCalendarReady(true);
        }
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setCalendarConnectLoading(false);
      setError(String(err?.message || "Failed to start Calendar connect"));
    }
  }, [calendarConnectLoading, stopCalendarPolling]);

  const handleCalendarDisconnect = useCallback(async () => {
    setError("");
    try {
      await disconnectBusinessPerformerCalendar();
      if (!mountedRef.current) return;
      setCalendarReady(false);
      clearGateChoice("calendar");
    } catch (err) {
      if (!mountedRef.current) return;
      setError(String(err?.message || "Failed to disconnect Calendar"));
    }
  }, []);

  const stopSlackPolling = useCallback(() => {
    if (!slackPollRef.current) return;
    if (typeof slackPollRef.current === "function") {
      slackPollRef.current();
    } else {
      clearInterval(slackPollRef.current);
    }
    slackPollRef.current = null;
  }, []);

  useEffect(() => {
    if (enabled) return;
    stopSlackPolling();
  }, [enabled, stopSlackPolling]);

  const refreshSlackStatus = useCallback(async () => {
    try {
      const status = await getSlackConnectionStatus();
      if (!mountedRef.current) return;
      setSlackReady(status?.connected === true && status?.has_send_scope === true);
      setSlackHasSendScope(status?.has_send_scope === true);
    } catch (_) {
      if (!mountedRef.current) return;
      setSlackReady(false);
      setSlackHasSendScope(false);
    }
  }, []);

  const handleSlackConnect = useCallback(async () => {
    if (slackConnectLoading) return;
    stopSlackPolling();
    setSlackConnectLoading(true);
    try {
      const authResp = await fetch(
        `${config.BACKEND_URL}/externalauth/slack/auth_url`,
        { method: "GET", credentials: "include" }
      );
      const authPayload = await authResp.json();
      const authUrl = authPayload.auth_url || "";
      if (!authUrl) {
        if (mountedRef.current) setSlackConnectLoading(false);
        return;
      }
      slackPollRef.current = startPerformerSlackOAuthFlow(authUrl, () => {
        if (mountedRef.current) {
          setSlackConnectLoading(false);
          setSlackReady(true);
          setSlackHasSendScope(true);
        }
      }, () => {
        if (mountedRef.current) setSlackConnectLoading(false);
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setSlackConnectLoading(false);
      setError(String(err?.message || "Failed to start Slack connect"));
    }
  }, [slackConnectLoading, stopSlackPolling]);

  const handleSlackDisconnect = useCallback(async () => {
    setError("");
    try {
      await disconnectSlack();
      if (!mountedRef.current) return;
      setSlackReady(false);
      setSlackHasSendScope(false);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(String(err?.message || "Failed to disconnect Slack"));
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refreshSlackStatus();
  }, [enabled, refreshSlackStatus]);

  const handleRunPerformer = useCallback(
    async (mode) => {
      if (runLaunchMode) return false;
      setRunLaunchMode(mode);
      setError("");
      setDriveStepNotice("");
      try {
        const driveUrls = parseDriveUrlsFromText(driveInput);
        const itemsToRun =
          mode === "unprocessed"
            ? pendingItems.filter(
                (it) => !itemHasStoredPerformerResult(it, aiResultsByItem, contextWorkflowId)
              )
            : pendingItems;
        if (itemsToRun.length === 0) {
          setDriveStepNotice(
            mode === "unprocessed"
              ? "Every open task already has AI results. Use “Draft all open tasks” to regenerate, or use per-row Run AI."
              : "No open tasks to run."
          );
          return false;
        }
        const actionItemInputs = await Promise.all(
          itemsToRun.map((it) => buildPerformerActionInput(it, contextWorkflowId))
        );
        if (!mountedRef.current) return;
        setRunTargetCount(itemsToRun.length);
        setStep(STEP_RUNNING);
        const contextFileIds = (contextFiles || [])
          .map((file) => String(file?.id || "").trim())
          .filter(Boolean);
        const { run_id } = await startPerformerRun({
          actionItems: actionItemInputs,
          driveUrls,
          contextFileIds,
        });
        if (!mountedRef.current) return;
        onBulkRunStart?.(actionItemInputs);
        startPolling(run_id);
        return true;
      } catch (err) {
        if (!mountedRef.current) return false;
        setRunTargetCount(null);
        setError(performerRunErrorMessage(err));
        setStep((prev) => (prev === STEP_RUNNING ? STEP_ERROR : STEP_DRIVE_INPUT));
        return false;
      } finally {
        if (mountedRef.current) setRunLaunchMode(null);
      }
    },
    [
      aiResultsByItem,
      contextWorkflowId,
      driveInput,
      contextFiles,
      onBulkRunStart,
      pendingItems,
      runLaunchMode,
      startPolling,
    ]
  );

  const handleReset = useCallback(() => {
    const wasRunning = step === STEP_RUNNING;
    stopPolling();
    setStep(STEP_IDLE);
    setError("");
    setDriveStepNotice("");
    setRunTargetCount(null);
    setRunLaunchMode(null);
    if (wasRunning) onBulkRunFinished?.();
  }, [stopPolling, step, onBulkRunFinished]);

  const unprocessedItems = pendingItems.filter(
    (it) => !itemHasStoredPerformerResult(it, aiResultsByItem, contextWorkflowId)
  );
  const unprocessedCount = unprocessedItems.length;
  const runningCount = runTargetCount != null ? runTargetCount : pendingItems.length;

  const skipGmailToDrive = useCallback(() => {
    setError("");
    setStep(STEP_DRIVE_INPUT);
  }, []);

  return {
    step,
    error,
    driveInput,
    setDriveInput,
    contextFiles,
    setContextFiles,
    driveStepNotice,
    setDriveStepNotice,
    runLaunchMode,
    gmailAuthUrl,
    driveAuthUrl,
    gmailReady,
    driveReady,
    calendarReady,
    calendarConnectLoading,
    gmailConnectLoading,
    slackReady,
    slackHasSendScope,
    slackConnectLoading,
    refreshGmailStatus,
    refreshCalendarStatus,
    refreshSlackStatus,
    handleGmailConnect,
    handleGmailDisconnect,
    handleDriveConnect,
    handleDriveDisconnect,
    handleCalendarConnect,
    handleCalendarDisconnect,
    handleSlackConnect,
    handleSlackDisconnect,
    handleRunPerformer,
    handleReset,
    skipGmailToDrive,
    unprocessedCount,
    runningCount,
    parseDriveUrlsFromText,
  };
};
